/**
 * API de autenticação.
 * Usado em b2b e b2b2c com o api client de cada app.
 */

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthError {
  code: string;
  message: string;
  detailing: string;
}

export interface LoginErrorResponse {
  errors: AuthError[];
  success: false;
  timestamp: string;
}

export interface AuthUser {
  token: string;
  email: string;
  userId: string;
  expiresAt: string;
  roles: string[];
  isGeneralAdmin: boolean;
  refreshToken: string;
  isTenantUserAdmin: boolean;
}

export interface LoginSuccessResponse {
  isSuccess: true;
  isFailure: boolean;
  timestamp: string;
  value: AuthUser;
}

export type LoginResponse = LoginSuccessResponse | LoginErrorResponse;

export type LoginFn = (
  credentials: LoginCredentials
) => Promise<LoginSuccessResponse>;

/** Opções do login (ex.: idioma para Accept-Language) */
export interface LoginOptions {
  /** Locale para o header Accept-Language (ex.: en-US, pt-BR). Default: en-US */
  acceptLanguage?: string;
}

/** Cliente mínimo necessário (post). Compatível com ApiClient do core. */
export interface AuthApiClient {
  post<T>(url: string, data?: unknown, config?: unknown): Promise<T>;
}

const AUTH_LOGIN_PATH = 'v1/Auth/login';
const AUTH_FORGOT_PASSWORD_PATH = 'v1/Auth/forgot-password';
const AUTH_RESET_PASSWORD_PATH = 'v1/Auth/reset-password';
const DEFAULT_ACCEPT_LANGUAGE = 'en-US';

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordSuccessResponse {
  success: true;
  messageCode: string;
}

export interface ResetPasswordErrorResponse {
  errors: AuthError[];
  success: false;
  timestamp: string;
}

/** Código de erro para fallback de login (ex.: rede). Apps devem mostrar t('login.error_generic'). */
export const LOGIN_ERROR_GENERIC = 'LOGIN_ERROR_GENERIC';
/** Resposta da API sem token/email. Apps podem mostrar t('login.error_generic'). */
export const LOGIN_INVALID_RESPONSE = 'LOGIN_INVALID_RESPONSE';

/**
 * Verifica se a resposta é um erro de autenticação
 */
function isLoginError(response: LoginResponse): response is LoginErrorResponse {
  return 'success' in response && response.success === false;
}

/**
 * Verifica se o objeto tem a forma mínima de AuthUser (tem token e email).
 */
function isAuthUserLike(obj: unknown): obj is Record<string, unknown> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'token' in obj &&
    typeof (obj as { token: unknown }).token === 'string' &&
    'email' in obj &&
    typeof (obj as { email: unknown }).email === 'string'
  );
}

/**
 * Converte objeto da API em AuthUser com campos obrigatórios preenchidos.
 */
function toAuthUser(obj: Record<string, unknown>): AuthUser {
  return {
    token: String(obj.token),
    email: String(obj.email),
    userId: obj.userId != null ? String(obj.userId) : '',
    expiresAt: obj.expiresAt != null ? String(obj.expiresAt) : '',
    roles: Array.isArray(obj.roles) ? (obj.roles as string[]) : [],
    isGeneralAdmin: Boolean(obj.isGeneralAdmin),
    refreshToken: obj.refreshToken != null ? String(obj.refreshToken) : '',
    isTenantUserAdmin: Boolean(obj.isTenantUserAdmin),
  };
}

/**
 * Normaliza a resposta do login: a API pode retornar { value: AuthUser } ou o AuthUser na raiz.
 */
function normalizeLoginResponse(raw: unknown): AuthUser {
  const asResponse = raw as LoginResponse;
  if (isLoginError(asResponse)) {
    const errorMessages = asResponse.errors.map(err => err.message).join(', ');
    throw new Error(errorMessages);
  }

  // Formato esperado pelo front: { isSuccess, value: AuthUser }
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const value = (raw as { value: unknown }).value;
    if (isAuthUserLike(value)) return toAuthUser(value);
  }

  // Formato direto da API: { token, email, userId, ... } na raiz
  if (isAuthUserLike(raw)) return toAuthUser(raw as Record<string, unknown>);

  throw new Error(LOGIN_INVALID_RESPONSE);
}

/**
 * Cria o cliente de API de autenticação.
 * Recebe o api do app (b2b/b2b2c) e retorna { login }.
 * Aceita resposta da API em formato { value: AuthUser } ou AuthUser na raiz.
 */
export function createAuthApi(api: AuthApiClient) {
  return {
    async login(
      credentials: LoginCredentials,
      options?: LoginOptions
    ): Promise<LoginSuccessResponse> {
      const acceptLanguage =
        options?.acceptLanguage?.trim() || DEFAULT_ACCEPT_LANGUAGE;
      try {
        const raw = await api.post<unknown>(
          `/${AUTH_LOGIN_PATH}`,
          credentials,
          {
            skipAuth: true,
            skipOnUnauthorized: true,
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': acceptLanguage,
            },
          }
        );

        const user = normalizeLoginResponse(raw);

        return {
          isSuccess: true,
          isFailure: false,
          timestamp: new Date().toISOString(),
          value: user,
        };
      } catch (error: unknown) {
        // Quando a API retorna 4xx, o axios rejeita e o body vem em error.response.data
        const data = (error as { response?: { data?: unknown } })?.response?.data;
        if (data && typeof data === 'object' && 'errors' in data && Array.isArray((data as LoginErrorResponse).errors)) {
          const errResponse = data as LoginErrorResponse;
          const errorMessages = errResponse.errors.map(err => err.message).join(', ');
          throw new Error(errorMessages);
        }
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(LOGIN_ERROR_GENERIC);
      }
    },

    /**
     * POST Auth/forgot-password — envia e-mail para receber token de redefinição.
     * baseURL é definida por quem cria o ApiClient (quem chama o endpoint).
     * Se a API retornar success: false (ex.: FORGOT_PASSWORD_EMAIL_NOT_FOUND), lança Error com a message.
     */
    async forgotPassword(
      payload: ForgotPasswordRequest,
      options?: LoginOptions
    ): Promise<void> {
      const acceptLanguage =
        options?.acceptLanguage?.trim() || DEFAULT_ACCEPT_LANGUAGE;
      try {
        const response = await api.post<unknown>(
          `/${AUTH_FORGOT_PASSWORD_PATH}`,
          payload,
          {
            skipAuth: true,
            skipOnUnauthorized: true,
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/plain',
              'Accept-Language': acceptLanguage,
            },
          }
        );
        const data = response as { success?: boolean; errors?: Array<{ message?: string }> };
        if (data?.success === false && Array.isArray(data?.errors) && data.errors.length > 0) {
          const message = data.errors[0].message ?? 'Request failed.';
          throw new Error(message);
        }
      } catch (error: unknown) {
        const data = (error as { response?: { data?: unknown } })?.response?.data;
        if (data && typeof data === 'object' && 'errors' in data && Array.isArray((data as { errors: Array<{ message?: string }> }).errors)) {
          const errResponse = data as { errors: Array<{ message?: string }> };
          const message = errResponse.errors[0]?.message ?? 'Request failed.';
          throw new Error(message);
        }
        if (error instanceof Error) throw error;
        throw error;
      }
    },

    /**
     * POST Auth/reset-password — redefine a senha com token recebido por e-mail.
     * Body: { token, newPassword }.
     * Em erro (ex.: PASSWORD_REQUIRES_NON_ALPHANUMERIC), lança Error com as mensagens dos errors.
     */
    async resetPassword(
      payload: ResetPasswordRequest,
      options?: LoginOptions
    ): Promise<ResetPasswordSuccessResponse> {
      const acceptLanguage =
        options?.acceptLanguage?.trim() || DEFAULT_ACCEPT_LANGUAGE;
      try {
        const response = await api.post<ResetPasswordSuccessResponse | ResetPasswordErrorResponse>(
          `/${AUTH_RESET_PASSWORD_PATH}`,
          payload,
          {
            skipAuth: true,
            skipOnUnauthorized: true,
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': acceptLanguage,
            },
          }
        );
        if (response.success === false) {
          const errResponse = response as ResetPasswordErrorResponse;
          const message = errResponse.errors?.map(e => e.message ?? e.code).join(', ') ?? 'Request failed.';
          throw new Error(message);
        }
        return response as ResetPasswordSuccessResponse;
      } catch (error: unknown) {
        const data = (error as { response?: { data?: unknown } })?.response?.data;
        if (data && typeof data === 'object' && 'errors' in data && Array.isArray((data as ResetPasswordErrorResponse).errors)) {
          const errResponse = data as ResetPasswordErrorResponse;
          const message = errResponse.errors.map(e => e.message ?? e.code).join(', ');
          throw new Error(message);
        }
        if (error instanceof Error) throw error;
        throw new Error(LOGIN_ERROR_GENERIC);
      }
    },
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
